import React, {useCallback, useContext, useMemo, useEffect, useState} from 'react';
import {Map, Set} from 'immutable';

import {Text, Icon} from '@albert-io/atomic';

import {GuideLevelModelV2} from 'resources/augmented/GuideLevel/GuideLevelModel.v2';

import {ContentDiscoveryContext} from 'client/components/ContentDiscovery/ContentDiscoveryProvider.react';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {useTracking} from 'react-tracking';
import sessionStore from 'client/Session/SessionStore';
import {GuideLevelMapType} from 'client/components/ContentDiscovery/SubjectAndGuideSelector/TopicsModal/GuideLevel.helpers';
import {getGuideLevelsQuestionCountQuery} from '../FilterGroup.queries';

import {ExpandableChip} from './ExpandableChip.react';

import './expandable-chip.scss';

interface SubjectChipProps {
  guideLevels: GuideLevelModelV2[];
}

interface GuideLevelCounts {
  [guideLevelId: string]: number;
}

const SubjectChip = ({guideLevels}: SubjectChipProps) => {
  const [guideLevelCounts, setGuideLevelCounts] = useState({} as GuideLevelCounts);
  const [countsLoaded, setCountsLoaded] = useState(false);
  const {
    selectedGuideLevels,
    setSelectedGuideLevels,
    setSelectedSubjects,
    selectedSubjects,
    setSelectedTopicsModalSubjects,
    setGuideCollection,
    questionDifficulties,
    questionTypes,
    standardCollection,
    hideAssessmentQuestions,
    hidePreviouslyAssigned,
    getSelectedStandardsAndSets
  } = useContext(ContentDiscoveryContext);

  const {trackEvent} = useTracking({
    type: 'chip'
  });

  const removeGuideSubject = useCallback(
    (subject) => {
      setSelectedTopicsModalSubjects((prevSubjects: Set<any>) => {
        const updatedList = prevSubjects.toList().filter((s) => s.getId() !== subject.getId());
        return Set(updatedList);
      });
      setGuideCollection((prevGuideCollection: Map<string, GuideLevelMapType>) => {
        const keyId = prevGuideCollection.findKey((guideLevels) => {
          if (!guideLevels) return false;
          const guideList = guideLevels.toArray();
          return guideList.some(
            (guide) => guide.guideLevel.getSubject().getId() === subject.getId()
          );
        });

        return prevGuideCollection.delete(keyId);
      });
    },
    [setSelectedTopicsModalSubjects, setGuideCollection]
  );

  const removeSelectedSubject = useCallback(
    (subject) => {
      setSelectedSubjects(selectedSubjects.filter((s) => s.getId() !== subject.getId()));
    },
    [setSelectedSubjects]
  );

  const subject = guideLevels[0].getSubject();
  const subjectIsSelected = useMemo(() => {
    return selectedSubjects.some((s) => s.getId() === subject.getId());
  }, [subject, selectedSubjects]);
  const removeGuideLevel = (guideLevelId) => {
    const updatedSelectedGuideLevels = selectedGuideLevels
      .filter((g) => g.getId() !== guideLevelId)
      .toList();
    if (subjectIsSelected) {
      removeSelectedSubject(subject);
    }
    setSelectedGuideLevels(updatedSelectedGuideLevels);
  };

  const removeAllGuideLevels = () => {
    let updatedSelectedGuideLevels = selectedGuideLevels;
    guideLevels.forEach((guideLevel) => {
      updatedSelectedGuideLevels = updatedSelectedGuideLevels
        .filter((g) => g.getId() !== guideLevel.getId())
        .toList();
    });
    if (subjectIsSelected) {
      removeSelectedSubject(subject);
    }
    setSelectedGuideLevels(updatedSelectedGuideLevels);
  };

  const subjectQuestionCount = useMemo(() => {
    return Object.values(guideLevelCounts).reduce((totalCount, guideLevelCount) => {
      return totalCount + guideLevelCount;
    }, 0);
  }, [guideLevelCounts]);

  useEffect(() => {
    const updateGuideLevelQuestionCounts = async () => {
      if (!sessionStore.isSuper()) {
        return;
      }

      setCountsLoaded(false);
      const {selectedStandardIds, selectedStandardSetIds} =
        getSelectedStandardsAndSets(standardCollection);

      const guideLevelIds = guideLevels.map((guideLevel) => guideLevel.getId()).join();

      const guideLevelsData = await getGuideLevelsQuestionCountQuery(
        guideLevelIds,
        questionDifficulties,
        questionTypes,
        selectedStandardIds,
        selectedStandardSetIds,
        hideAssessmentQuestions,
        hidePreviouslyAssigned
      ).getResourcePromise();

      const updatedCounts = {} as GuideLevelCounts;

      guideLevelsData.getCollection().forEach((guideLevel) => {
        updatedCounts[guideLevel.getId()] =
          guideLevel.getMeta().getContentDiscoveryQuestionCount() || 0;
      });
      setCountsLoaded(true);
      setGuideLevelCounts(updatedCounts);
    };

    updateGuideLevelQuestionCounts();
  }, [
    questionDifficulties,
    questionTypes,
    getSelectedStandardsAndSets,
    hideAssessmentQuestions,
    hidePreviouslyAssigned,
    standardCollection,
    guideLevels
  ]);

  return (
    <ExpandableChip
      id={subject.getId()}
      className='u-mar-b_1'
      text={subject.getName()}
      onExpandToggle={(on) => {
        trackEvent({
          event: 'click',
          type: on ? 'collapse' : 'expand',
          content: `subject: ${subject.getName()}`
        });
      }}
      onCancel={() => {
        trackEvent({event: 'click', content: `Subject: ${subject.getName()}`});
        removeAllGuideLevels();
        removeGuideSubject(subject);
      }}
      count={subjectQuestionCount}
      showCount={countsLoaded}
    >
      {guideLevels.map((guideLevel) => {
        const guideName = guideLevel.getName();
        return (
          <li title={guideName} className='expandable-chip__section-item u-pad_0 u-pad-l_1'>
            <Text color='inherit' as='div' className='u-flex_1' size='xs'>
              <MarkdownRendererV2 text={guideName} />
            </Text>
            {
              /*
                TODO: Enable for all users once feature is complete
                See https://github.com/albert-io/nexus/issues/7059
              */
              sessionStore.isSuper() && countsLoaded && (
                <Text
                  className='expandable-chip_question-count u-flex_1'
                  color='inherit'
                  as='div'
                  size='xs'
                >
                  {`(${guideLevelCounts[guideLevel.getId()]})`}
                </Text>
              )
            }
            <button
              className='expandable-chip__button'
              type='button'
              onClick={() => {
                trackEvent({
                  event: 'click',
                  content: `guide Level: ${guideLevel.getName()}`
                });
                removeGuideLevel(guideLevel.getId());
                if (guideLevels.length === 1) {
                  // if last guide level is being removed we can remove the subject
                  removeGuideSubject(subject);
                }
              }}
              aria-label={`remove ${guideName}`}
            >
              <Icon icon='times' />
            </button>
          </li>
        );
      })}
    </ExpandableChip>
  );
};

export const SubjectChipGroup = () => {
  const {selectedGuideLevels} = useContext(ContentDiscoveryContext);

  const subjectGuideMap = useMemo(() => {
    let newSubjectGuideMap = Map<string, GuideLevelModelV2[]>({});
    selectedGuideLevels.forEach((guideLevel) => {
      const subjectId = guideLevel.getSubject().getId();
      if (newSubjectGuideMap.has(subjectId)) {
        newSubjectGuideMap = newSubjectGuideMap.updateIn([subjectId], (guideLevels) => {
          const newGuideLevelList = guideLevels;
          newGuideLevelList.push(guideLevel);
          return newGuideLevelList;
        });
      } else {
        newSubjectGuideMap = newSubjectGuideMap.set(subjectId, [guideLevel]);
      }
    });
    return newSubjectGuideMap;
  }, [selectedGuideLevels]);

  return subjectGuideMap.toArray().length > 0 ? (
    <div className='u-mar-t_1'>
      {subjectGuideMap.toArray().map((guideLevels) => {
        return <SubjectChip guideLevels={guideLevels} />;
      })}
    </div>
  ) : null;
};
